<template>
	<div class="content">
		<base-header class="pb-6">
			<div class="row align-items-center py-4">
				<div class="col-lg-6 col-7">
					<h6 class="h2 text-white d-inline-block mb-0">{{ $t("pos.menu.report_top_spenders") }}</h6>
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</div>
				<div class="col-lg-6 col-5 text-right">
					<base-button size="md" type="neutral" :disabled="loading" @click="exportXlsx">{{ $t("pos.common.export") }}</base-button>
				</div>
			</div>
		</base-header>

		<div class="container-fluid mt--6">
			<pos-install-alert :show="notInstalled"></pos-install-alert>
			<!-- table edge-to-edge in the card (as /backoffice/orders-overview): card body px-0, everything else in px-4 -->
			<card class="no-border-card" body-classes="px-0">
				<div class="px-4">
					<pos-report-filter v-model="filter" @search="load">
						<el-input v-model="q" size="small" clearable class="mt-2" style="max-width: 240px" :placeholder="$t('pos.report.mid_search')" @keyup.enter.native="load" @clear="load"></el-input>
					</pos-report-filter>

					<h5 class="mb-2">{{ $t("pos.report.chart_top") }}</h5>
					<pos-bar-chart :rows="chartRows" :series="chartSeries" :empty-text="$t('pos.report.no_data')" class="mb-4"></pos-bar-chart>
				</div>

				<el-table :data="rows" v-loading="loading" header-row-class-name="thead-light" class="bon-table pos-table pos-click-rows" @row-click="open">
					<el-table-column :label="$t('pos.report.rank')" prop="rank" width="80" align="center"></el-table-column>
					<el-table-column :label="$t('pos.report.mid')" prop="mid_text" width="110"></el-table-column>
					<el-table-column :label="$t('pos.report.event')" prop="event_name" min-width="160"></el-table-column>
					<el-table-column :label="$t('pos.report.phone')" prop="phone" width="125" class-name="pos-nowrap"></el-table-column>
					<el-table-column :label="$t('pos.report.name')" prop="name" min-width="130"></el-table-column>
					<el-table-column :label="$t('pos.report.bill_count')" width="110" align="right">
						<template slot-scope="{ row }">{{ int(row.bill_count) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.total_spent')" min-width="130" align="right">
						<template slot-scope="{ row }"><strong>{{ money(row.total) }}</strong></template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.avg_bill')" min-width="120" align="right">
						<template slot-scope="{ row }">{{ money(row.avg_bill) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.first_bill')" prop="first_bill_at" min-width="160"></el-table-column>
					<el-table-column :label="$t('pos.report.last_bill')" prop="last_bill_at" min-width="160"></el-table-column>
				</el-table>
			</card>
		</div>
	</div>
</template>

<script>
import { Table, TableColumn } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { PosService } from "@/resource";
import { posMoney, posInt } from "@/utils/pos";
import posPageMixin, { posDefaultFilter } from "./posPageMixin";
import PosReportFilter from "./PosReportFilter.vue";
import PosInstallAlert from "./PosInstallAlert.vue";
import PosBarChart from "./PosBarChart.vue";

export default {
	name: "PosReportTopSpenders",
	mixins: [posPageMixin],
	components: { RouteBreadCrumb, PosReportFilter, PosInstallAlert, PosBarChart, [Table.name]: Table, [TableColumn.name]: TableColumn },
	data() {
		return { filter: posDefaultFilter(), q: "", rows: [] };
	},
	computed: {
		chartSeries() {
			return [{ key: "total", label: this.$t("pos.report.total_spent"), color: "#f5365c" }];
		},
		chartRows() {
			return this.rows.slice(0, 10).map((r) => ({ label: `${r.rank}. #${r.mid_text}${r.name ? " " + r.name : ""}`, values: { total: Number(r.total) || 0 } }));
		}
	},
	mounted() {
		this.load();
	},
	methods: {
		money: posMoney,
		int: posInt,
		params(extra) {
			const p = this.posParams(this.filter);
			if (!p) return null;
			// buyer ID ("31" / "#0031") or phone prefix — the API matches either
			const q = this.q.trim();
			if (q) p.q = q;
			return { ...p, ...(extra || {}) };
		},
		async load() {
			const p = this.params({ limit: 50 });
			if (!p) return;
			this.loading = true;
			try {
				const res = await PosService.PosReportTopSpenders(p);
				this.rows = (res.data && res.data.Data) || [];
				this.notInstalled = false;
			} catch (err) {
				this.rows = [];
				this.posError(err);
			} finally {
				this.loading = false;
			}
		},
		open(row) {
			const p = this.params() || {};
			// a buyer ID is per event -> that event's buyer report, opened on this buyer's bills
			this.$router.push({ name: "PosReportEventMembers", query: { event_id: String(row.event_id), mid: row.mid_text, include_test: p.include_test ? "1" : "0" } });
		},
		async exportXlsx() {
			const p = this.params({ export: 1 });
			if (!p) return;
			this.loading = true;
			try {
				const res = await PosService.PosReportTopSpenders(p);
				const t = (k) => this.$t(k);
				this.posExport("top-spenders", p, [{
					name: t("pos.menu.report_top_spenders"),
					rows: (res.data && res.data.Data) || [],
					columns: [
						{ label: t("pos.report.rank"), value: "rank" },
						{ label: t("pos.report.mid"), value: "mid_text" },
						{ label: t("pos.report.event"), value: "event_name" },
						{ label: t("pos.report.phone"), value: "phone" },
						{ label: t("pos.report.name"), value: "name" },
						{ label: t("pos.report.bill_count"), value: "bill_count" },
						{ label: t("pos.report.total_spent"), value: (r) => Number(r.total) },
						{ label: t("pos.report.avg_bill"), value: (r) => Number(r.avg_bill) },
						{ label: t("pos.report.first_bill"), value: "first_bill_at" },
						{ label: t("pos.report.last_bill"), value: "last_bill_at" }
					]
				}]);
			} catch (err) {
				this.posError(err);
			} finally {
				this.loading = false;
			}
		}
	}
};
</script>

<style>
.pos-click-rows .el-table__row {
	cursor: pointer;
}
</style>
